import { Document, Packer, Paragraph, TextRun, AlignmentType, ImageRun } from 'docx';
import * as fs from 'fs';

interface ChecklistItem {
  id: string;
  text: string;
}

interface ChecklistLetterData {
  recipientName: string;
  recipientEmail: string;
  recipientAddress: string;
  checklistItems: ChecklistItem[];
}

export const generateChecklistWordDocument = async (letterData: ChecklistLetterData) => {
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const addressLines = letterData.recipientAddress ? letterData.recipientAddress.split('\n') : [];

  const docContent = [
    // Logo and Header
    new Paragraph({
      children: [
        new TextRun({
          text: "CONTRACT FUNDING GROUP, LLC",
          bold: true,
          size: 32,
          color: "8B4513"
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 100 }
    }),
    new Paragraph({
      children: [
        new TextRun({
          text: "Making real estate investments simple",
          bold: true,
          size: 22,
          color: "FF6B35"
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 }
    }),

    // Date
    new Paragraph({
      children: [
        new TextRun({
          text: currentDate,
          size: 20
        })
      ],
      spacing: { after: 200 }
    }),

    // Recipient Name
    new Paragraph({
      children: [
        new TextRun({
          text: letterData.recipientName || '[Recipient Name]',
          bold: true,
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),

    // Recipient Address
    ...addressLines.map(line => 
      new Paragraph({
        children: [
          new TextRun({
            text: line,
            size: 20
          })
        ],
        spacing: { after: 50 }
      })
    ),

    new Paragraph({
      text: "",
      spacing: { after: 200 }
    }),

    // Greeting
    new Paragraph({
      children: [
        new TextRun({
          text: `Dear ${letterData.recipientName || '[Recipient Name]'},`,
          size: 22
        })
      ],
      spacing: { after: 200 }
    }),

    // Body Paragraph 1
    new Paragraph({
      children: [
        new TextRun({
          text: "Thank you for choosing Contract Funding Group. We strive to make the closing process efficient and painless for all parties involved.",
          size: 22
        })
      ],
      spacing: { after: 200 }
    }),

    // Body Paragraph 2
    new Paragraph({
      children: [
        new TextRun({
          text: "Below is a checklist of items needed for your cash offer underwriting. Please gather these documents to ensure a smooth and timely closing process:",
          size: 22
        })
      ],
      spacing: { after: 300 }
    }),

    // Checklist Header
    new Paragraph({
      children: [
        new TextRun({
          text: "Required Documentation Checklist:",
          bold: true,
          size: 24,
          color: "8B4513"
        })
      ],
      spacing: { after: 200 }
    }),

    // Checklist Items
    ...letterData.checklistItems.map((item, index) => 
      new Paragraph({
        children: [
          new TextRun({
            text: `☐  ${item.text}`,
            size: 22
          })
        ],
        spacing: { after: 150 }
      })
    ),

    // Closing Paragraph 1
    new Paragraph({
      children: [
        new TextRun({
          text: "Please review this checklist carefully and gather all required documents. If you have any questions or need clarification on any item, please don't hesitate to contact us.",
          size: 22
        })
      ],
      spacing: { before: 300, after: 200 }
    }),

    // Closing Paragraph 2
    new Paragraph({
      children: [
        new TextRun({
          text: "We appreciate your business and look forward to working with you to complete this transaction.",
          size: 22
        })
      ],
      spacing: { after: 400 }
    }),

    // Signature
    new Paragraph({
      children: [
        new TextRun({
          text: "Sincerely,",
          size: 22
        })
      ],
      spacing: { after: 600 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: "_________________________________",
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: "Contract Funding Group Team",
          bold: true,
          size: 22
        })
      ],
      spacing: { after: 100 }
    }),

    new Paragraph({
      children: [
        new TextRun({
          text: "Contract Funding Group | Real Estate Investment Solutions",
          size: 18,
          color: "666666"
        })
      ],
      spacing: { before: 400 }
    }),
  ];

  const doc = new Document({
    sections: [{
      properties: {},
      children: docContent
    }]
  });

  return await Packer.toBlob(doc);
};
